import React, { useCallback, useMemo, useState } from 'react'

import { formatDistanceToNow } from 'date-fns'
import {
  type Control,
  Controller,
  type FieldPath,
  FieldValues,
  Path,
} from 'react-hook-form'
import {
  HiChevronUpDown,
  HiMiniCheck,
  HiMiniPlus,
  HiMiniXMark,
} from 'react-icons/hi2'
import { LuCalendar } from 'react-icons/lu'
import { twMerge } from 'tailwind-merge'

import { Badge } from '@/components/common/BadgeV2'
import Button from '@/components/common/Button'
import {
  Combobox,
  ComboboxContent,
  ComboboxGroup,
  ComboboxInput,
  ComboboxList,
  ComboboxSelectableItem,
  ComboboxTrigger,
} from '@/components/common/ComboboxV2'
import DateTimePickerV2 from '@/components/common/DateTimePickerV2'
import { DaypickerCalendar } from '@/components/common/Daypicker'
import Popover from '@/components/common/PopoverV2'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/common/Select'
import { Input, TEXT_INPUT_CLASSNAME } from '@/components/common/TextInputV2'
import { BasicBlueButton } from '@/components/library-v2/ui/Button'
import {
  EntityCombobox,
  CompanyCombobox,
  DepartmentCombobox,
  FileCombobox,
  PersonCombobox,
  SelectOrCreate,
} from '@/components/library-v2/ui/Combobox'
import { ActivityDetailsEditor } from '@/components/library-v2/ui/EntityActivity/ActivityDetailsEditor'
import { TagComboboxMulti } from '@/components/tags/TagCombobox'
import { TagList } from '@/components/tags/TagList'

import { COLOR_CLASSNAMES } from '@/constants/color'

import { useNAICSCodeSearch } from '@/hooks/search/useNAICSCodeSearch'
import { usePSCCodeSearch } from '@/hooks/search/usePSCCodeSearch'

import type { Color } from '@/types/Color'
import type { NAICS, PSC } from '@/types/federal-contract/Api'
import type { UnhydratedFile } from '@/types/File'
import type { UUID } from '@/types/Id'
import type {
  EditableField,
  EntityAgency,
  EntityCompany,
  EntityCompanyDepartment,
  EntityDateFieldMode,
  EntityPerson,
  FieldType,
} from '@/types/library/Entity'

import {
  formatEntityDateFieldValue,
  parseEntityDateFieldValue,
  serializeEntityDateFieldValue,
} from './dateField'

// Field Components

interface Field<
  U extends FieldValues,
  K extends Path<U> = Path<U>,
> extends EditableField<U> {}

interface FieldInputProps<U extends FieldValues, K extends Path<U> = Path<U>> {
  field: Field<U, K>
  control: Control<U>
  companies?: EntityCompany[]
  people?: EntityPerson[]
  agencies?: EntityAgency[]
  departments?: EntityCompanyDepartment[]
  files?: UnhydratedFile[]
}

interface CompanyReferenceFieldProps<
  U extends FieldValues,
  K extends Path<U> = Path<U>,
> extends FieldInputProps<U, K> {
  value: UUID | null
  onChange: (value: UUID) => void
}

const CompanyReferenceField = <U extends FieldValues, K extends Path<U>>({
  value,
  onChange,
  companies,
}: CompanyReferenceFieldProps<U, K>) => {
  return (
    <div className="flex items-center w-full">
      <CompanyCombobox
        value={value}
        onChange={onChange}
        companies={companies}
      />
    </div>
  )
}

interface PersonReferenceFieldProps<
  U extends FieldValues,
  K extends Path<U> = Path<U>,
> extends FieldInputProps<U, K> {
  value: UUID | null
  onChange: (value: UUID | null) => void
}

const PersonReferenceField = <U extends FieldValues, K extends Path<U>>({
  value,
  onChange,
  people,
}: PersonReferenceFieldProps<U, K>) => {
  return (
    <div className="flex items-center w-full">
      <PersonCombobox value={value} onChange={onChange} people={people} />
    </div>
  )
}

interface AgencyReferenceFieldProps<
  U extends FieldValues,
  K extends Path<U> = Path<U>,
> extends FieldInputProps<U, K> {
  value: UUID | null
  onChange: (value: UUID | null) => void
}

const AgencyReferenceField = <U extends FieldValues, K extends Path<U>>({
  value,
  onChange,
  agencies,
  field,
}: AgencyReferenceFieldProps<U, K>) => {
  return (
    <div className="flex items-center w-full">
      <EntityCombobox
        value={value}
        onChange={onChange}
        entities={agencies}
        placeholder={field.placeholder ?? 'Select agency...'}
      />
    </div>
  )
}

interface DepartmentReferenceFieldProps<
  U extends FieldValues,
  K extends Path<U> = Path<U>,
> extends FieldInputProps<U, K> {
  value: UUID | null
  onChange: (value: UUID) => void
}

const DepartmentReferenceField = <U extends FieldValues, K extends Path<U>>({
  value,
  onChange,
  departments,
}: DepartmentReferenceFieldProps<U, K>) => {
  return (
    <div className="flex items-center w-full">
      <DepartmentCombobox
        value={value}
        onChange={onChange}
        departments={departments}
      />
    </div>
  )
}

interface FileReferenceFieldProps<
  U extends FieldValues,
  K extends Path<U> = Path<U>,
> extends FieldInputProps<U, K> {
  value: UUID | null
  onChange: (value: UUID) => void
}

const FileReferenceField = <U extends FieldValues, K extends Path<U>>({
  value,
  onChange,
  files,
}: FileReferenceFieldProps<U, K>) => {
  return (
    <div className="flex items-center w-full">
      <FileCombobox value={value} onChange={onChange} files={files} />
    </div>
  )
}

interface StringArrayFieldProps<
  U extends FieldValues,
  K extends Path<U> = Path<U>,
> extends FieldInputProps<U, K> {
  value: string[] | null
  onChange: (value: string[] | null) => void
}

const StringArrayField = <U extends FieldValues, K extends Path<U>>({
  field,
  value,
  onChange,
}: StringArrayFieldProps<U, K>) => {
  const values = value || []

  const handleAdd = useCallback(() => {
    onChange([...values, ''])
  }, [values, onChange])

  const handleRemove = useCallback(
    (index: number) => () => {
      const newValues = values.filter((_, i) => i !== index)
      onChange(newValues.length > 0 ? newValues : null)
    },
    [values, onChange],
  )

  const handleChange = useCallback(
    (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      const newValues = [...values]
      newValues[index] = newValue
      onChange(newValues)
    },
    [values, onChange],
  )

  return (
    <div className="flex flex-col gap-2">
      {values.map((item, index) => (
        <div key={index} className="flex items-center gap-2">
          <Input
            value={item}
            onChange={handleChange(index)}
            placeholder={field.placeholder ?? 'Enter value'}
            className="dark:bg-default-dark flex-1"
          />
          <Button
            type="button"
            onClick={handleRemove(index)}
            className="text-red-600 dark:text-red-400 hover:opacity-70 text-sm font-medium"
            variant="danger-compact"
          >
            Remove
          </Button>
        </div>
      ))}
      <BasicBlueButton type="button" onClick={handleAdd}>
        <HiMiniPlus />
        <span>Add {field.label}</span>
      </BasicBlueButton>
    </div>
  )
}

interface CommaSeparatedArrayFieldProps<
  U extends FieldValues,
  K extends Path<U> = Path<U>,
> extends FieldInputProps<U, K> {
  value: string[] | null
  onChange: (value: string[] | null) => void
}

const arrayToCommaSeparatedValue = (value: string[] | null) =>
  value ? value.join(', ') : ''

const CommaSeparatedArrayField = <U extends FieldValues, K extends Path<U>>({
  field,
  value,
  onChange,
}: CommaSeparatedArrayFieldProps<U, K>) => {
  const defaultValue = arrayToCommaSeparatedValue(value)

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const input = event.currentTarget.value.trim()
      if (input === '') {
        onChange(null)
      } else {
        const array = input
          .split(',')
          .map((item) => item.trim())
          .filter((item) => item !== '')
        onChange(array.length > 0 ? array : null)
      }
    },
    [onChange],
  )

  return (
    <Input
      key={defaultValue}
      defaultValue={defaultValue}
      onBlur={handleBlur}
      placeholder={field.placeholder ?? 'Provide a comma-separated list...'}
      className="dark:bg-default-dark"
    />
  )
}

const splitArrayInput = (input: string) =>
  input
    .split(/[\n,]+/)
    .map((item) => item.trim())
    .filter(Boolean)

const normalizeArrayInputValue = (fieldKey: string, value: string): string => {
  const trimmed = value.trim()
  if (fieldKey === 'naics_codes' || fieldKey === 'psc_codes') {
    return trimmed
      .split(/\s+-\s+|:/)[0]
      .trim()
      .toUpperCase()
  }
  return trimmed
}

const valuesWithAddedItems = (currentValues: string[], newValues: string[]) => {
  const existing = new Set(currentValues.map((item) => item.toLowerCase()))
  const additions = newValues.filter((item) => {
    const normalized = item.toLowerCase()
    if (existing.has(normalized)) return false
    existing.add(normalized)
    return true
  })
  return [...currentValues, ...additions]
}

const MULTI_COMBOBOX_CONTENT_CLASSNAME = 'w-[min(420px,calc(100vw-2rem))] p-0'

interface TagInputFieldProps<
  U extends FieldValues,
  K extends Path<U> = Path<U>,
> extends FieldInputProps<U, K> {
  value: string[] | null
  onChange: (value: string[] | null) => void
}

const TagInputField = <U extends FieldValues, K extends Path<U>>({
  field,
  value,
  onChange,
}: TagInputFieldProps<U, K>) => {
  const [inputValue, setInputValue] = useState<string>('')

  const values = value ?? []

  const commitValues = useCallback(
    (input: string) => {
      const newValues = splitArrayInput(input).map((item) =>
        normalizeArrayInputValue(String(field.key), item),
      )
      if (newValues.length === 0) return
      const updated = valuesWithAddedItems(values, newValues)
      onChange(updated.length > 0 ? updated : null)
      setInputValue('')
    },
    [field.key, onChange, values],
  )

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(event.target.value)
    },
    [],
  )

  const handleInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter' && event.key !== ',') return
      event.preventDefault()
      commitValues(inputValue)
    },
    [commitValues, inputValue],
  )

  const handleInputBlur = useCallback(() => {
    commitValues(inputValue)
  }, [commitValues, inputValue])

  const handleAddClick = useCallback(() => {
    commitValues(inputValue)
  }, [commitValues, inputValue])

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedText = event.clipboardData.getData('text')
      if (!/[\n,]/.test(pastedText)) return
      event.preventDefault()
      commitValues(pastedText)
    },
    [commitValues],
  )

  const handleRemove = useCallback(
    (itemToRemove: string) => () => {
      const updated = values.filter((item) => item !== itemToRemove)
      onChange(updated.length > 0 ? updated : null)
    },
    [onChange, values],
  )

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2">
        <Input
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          onBlur={handleInputBlur}
          onPaste={handlePaste}
          placeholder={field.placeholder ?? 'Add value...'}
          className="dark:bg-default-dark"
        />
        <Button
          type="button"
          variant="soft-compact"
          onClick={handleAddClick}
          disabled={inputValue.trim().length === 0}
          className="h-9 flex-shrink-0"
        >
          <HiMiniPlus className="h-4 w-4" />
          Add
        </Button>
      </div>
      {values.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {values.map((item) => (
            <Badge
              key={item}
              variant="outline"
              className="flex max-w-full items-center gap-1 pr-1"
            >
              <span className="max-w-48 truncate">{item}</span>
              <button
                type="button"
                aria-label={`Remove ${item}`}
                onClick={handleRemove(item)}
                className="rounded-sm p-0.5 text-primary-600 hover:bg-primary-300 hover:text-primary-900 dark:text-primary-300 dark:hover:bg-primary-700 dark:hover:text-primary-100"
              >
                <HiMiniXMark size={12} />
              </button>
            </Badge>
          ))}
        </div>
      ) : null}
    </div>
  )
}

interface MultiComboboxFieldProps<
  U extends FieldValues,
  K extends Path<U> = Path<U>,
> extends FieldInputProps<U, K> {
  value: string[] | null
  onChange: (value: string[] | null) => void
}

interface MultiComboboxOption {
  value: string
  label: string
  description?: string
  color?: Color
}

const OptionColorDot: React.FC<{
  color?: Color
  className?: string
}> = ({ color, className }) => {
  if (!color) return null

  return (
    <span
      aria-hidden={true}
      className={twMerge(
        COLOR_CLASSNAMES[color],
        'size-2 shrink-0 rounded-full border-0 p-0',
        className,
      )}
    />
  )
}

const getMultiComboboxOptions = (
  field: Pick<
    EditableField<Record<string, never>>,
    'options' | 'optionDescriptions' | 'optionColors'
  >,
): MultiComboboxOption[] => {
  return Array.from(new Set(field.options ?? [])).map((option) => ({
    value: option,
    label: option,
    description: field.optionDescriptions?.[option],
    color: field.optionColors?.[option],
  }))
}

const optionMatchesQuery = (option: MultiComboboxOption, query: string) => {
  const normalizedQuery = query.trim().toLowerCase()
  if (!normalizedQuery) return true
  return [option.value, option.label, option.description]
    .filter(Boolean)
    .some((item) => item?.toLowerCase().includes(normalizedQuery))
}

const MultiComboboxField = <U extends FieldValues, K extends Path<U>>({
  field,
  value,
  onChange,
}: MultiComboboxFieldProps<U, K>) => {
  const [open, setOpen] = useState<boolean>(false)
  const [query, setQuery] = useState<string>('')

  const values = value ?? []
  const options = getMultiComboboxOptions(field)
  const filteredOptions = options.filter((option) =>
    optionMatchesQuery(option, query),
  )
  const allowCustom = field.allowCustom ?? false
  const customValue = normalizeArrayInputValue(String(field.key), query)
  const customExists =
    customValue.length === 0 ||
    values.some((item) => item.toLowerCase() === customValue.toLowerCase()) ||
    options.some(
      (option) => option.value.toLowerCase() === customValue.toLowerCase(),
    )
  const canCreate = allowCustom && !customExists

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) setQuery('')
  }, [])

  const setValues = useCallback(
    (nextValues: string[]) => {
      onChange(nextValues.length > 0 ? nextValues : null)
    },
    [onChange],
  )

  const toggleValue = useCallback(
    (item: string) => () => {
      const normalized = item.toLowerCase()
      const exists = values.some((value) => value.toLowerCase() === normalized)
      if (exists) {
        setValues(values.filter((value) => value.toLowerCase() !== normalized))
        return
      }
      setValues([...values, item])
    },
    [setValues, values],
  )

  const handleCreate = useCallback(() => {
    if (!canCreate) return
    const updated = valuesWithAddedItems(values, [customValue])
    setValues(updated)
    setQuery('')
  }, [canCreate, customValue, setValues, values])

  const handleClearAll = useCallback(() => {
    setValues([])
  }, [setValues])

  return (
    <div className="flex flex-col gap-2">
      <Combobox open={open} onOpenChange={handleOpenChange}>
        <ComboboxTrigger asChild>
          <button
            type="button"
            className={twMerge(
              TEXT_INPUT_CLASSNAME,
              'flex items-center justify-between gap-2 text-left',
            )}
          >
            <span
              className={twMerge(
                'truncate',
                values.length > 0
                  ? 'text-primary-900 dark:text-primary-100'
                  : 'text-primary-500 dark:text-primary-400',
              )}
            >
              {values.length > 0
                ? `${values.length} selected`
                : (field.placeholder ?? 'Search and add...')}
            </span>
            <HiChevronUpDown size={16} className="flex-shrink-0" />
          </button>
        </ComboboxTrigger>
        <ComboboxContent
          align="start"
          sideOffset={2}
          collisionPadding={8}
          className={MULTI_COMBOBOX_CONTENT_CLASSNAME}
          shouldFilter={false}
        >
          <ComboboxInput
            value={query}
            onValueChange={setQuery}
            placeholder={field.placeholder ?? 'Search...'}
            className="placeholder:text-sm"
          />
          <ComboboxList className="max-h-72 p-1">
            {filteredOptions.length === 0 && !canCreate ? (
              <div className="px-2 py-3 text-sm text-primary-500 dark:text-primary-400">
                No matching options.
              </div>
            ) : (
              <ComboboxGroup>
                {filteredOptions.map((option) => {
                  const isSelected = values.some(
                    (item) => item.toLowerCase() === option.value.toLowerCase(),
                  )
                  return (
                    <ComboboxSelectableItem
                      key={option.value}
                      value={option.value}
                      isSelected={isSelected}
                      onSelect={toggleValue(option.value)}
                    >
                      <div className="flex min-w-0 items-start gap-2">
                        <OptionColorDot
                          color={option.color}
                          className="mt-1.5"
                        />
                        <span className="flex min-w-0 flex-col items-start gap-0.5">
                          <span className="font-medium">{option.label}</span>
                          {option.description ? (
                            <span className="text-xs text-primary-500 dark:text-primary-400">
                              {option.description}
                            </span>
                          ) : null}
                        </span>
                      </div>
                    </ComboboxSelectableItem>
                  )
                })}
                {canCreate ? (
                  <ComboboxSelectableItem
                    value={`__create__:${customValue}`}
                    isSelected={false}
                    onSelect={handleCreate}
                  >
                    <span>Add &quot;{customValue}&quot;</span>
                  </ComboboxSelectableItem>
                ) : null}
              </ComboboxGroup>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
      {values.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {values.map((item) => {
            const option = options.find(
              (candidate) =>
                candidate.value.toLowerCase() === item.toLowerCase(),
            )
            return (
              <Badge
                key={item}
                variant="outline"
                title={option?.description}
                className="flex max-w-full items-center gap-1 pr-1"
              >
                <OptionColorDot color={option?.color} />
                <span className="max-w-48 truncate">
                  {option?.label ?? item}
                </span>
                <button
                  type="button"
                  aria-label={`Remove ${option?.label ?? item}`}
                  onClick={toggleValue(item)}
                  className="rounded-sm p-0.5 text-primary-600 hover:bg-primary-300 hover:text-primary-900 dark:text-primary-300 dark:hover:bg-primary-700 dark:hover:text-primary-100"
                >
                  <HiMiniXMark size={12} />
                </button>
              </Badge>
            )
          })}
          <button
            type="button"
            onClick={handleClearAll}
            className="text-xs font-medium text-blue-600 hover:opacity-70 dark:text-blue-400"
          >
            Clear all
          </button>
        </div>
      ) : null}
    </div>
  )
}

type CodeSearchItem = NAICS | PSC

interface CodeSearchMultiComboboxFieldProps<
  U extends FieldValues,
  K extends Path<U> = Path<U>,
> extends FieldInputProps<U, K> {
  value: string[] | null
  onChange: (value: string[] | null) => void
  codeType: 'NAICS' | 'PSC'
  options: CodeSearchItem[]
  query: string
  setQuery: (query: string) => void
  isFetching: boolean
}

const codeSearchItemLabel = (item: CodeSearchItem) =>
  item.description ? `${item.code} - ${item.description}` : item.code

const CodeSearchMultiComboboxField = <
  U extends FieldValues,
  K extends Path<U>,
>({
  field,
  value,
  onChange,
  codeType,
  options,
  query,
  setQuery,
  isFetching,
}: CodeSearchMultiComboboxFieldProps<U, K>) => {
  const [open, setOpen] = useState(false)
  const values = value ?? []
  const selectedSet = useMemo(() => new Set(values), [values])
  const optionsByCode = useMemo(
    () => new Map(options.map((item) => [item.code, item])),
    [options],
  )
  const customValue = normalizeArrayInputValue(String(field.key), query)
  const customExists =
    customValue.length === 0 ||
    selectedSet.has(customValue) ||
    options.some(
      (option) => option.code.toLowerCase() === customValue.toLowerCase(),
    )
  const canCreate = field.allowCustom === true && !customExists

  const setValues = useCallback(
    (nextValues: string[]) => {
      onChange(nextValues.length > 0 ? nextValues : null)
    },
    [onChange],
  )

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen)
      if (!nextOpen) setQuery('')
    },
    [setQuery],
  )

  const toggleCode = useCallback(
    (code: string) => () => {
      if (selectedSet.has(code)) {
        setValues(values.filter((item) => item !== code))
        return
      }
      setValues([...values, code])
    },
    [selectedSet, setValues, values],
  )

  const handleCreate = useCallback(() => {
    if (!canCreate) return
    setValues(valuesWithAddedItems(values, [customValue]))
    setQuery('')
  }, [canCreate, customValue, setQuery, setValues, values])

  const handleClearAll = useCallback(() => {
    setValues([])
  }, [setValues])

  return (
    <div className="flex flex-col gap-2">
      <Combobox open={open} onOpenChange={handleOpenChange}>
        <ComboboxTrigger asChild>
          <button
            type="button"
            className={twMerge(
              TEXT_INPUT_CLASSNAME,
              'flex items-center justify-between gap-2 text-left',
            )}
          >
            <span
              className={twMerge(
                'truncate',
                values.length > 0
                  ? 'text-primary-900 dark:text-primary-100'
                  : 'text-primary-500 dark:text-primary-400',
              )}
            >
              {values.length > 0
                ? `${values.length} selected`
                : (field.placeholder ?? `Search ${codeType} codes...`)}
            </span>
            <HiChevronUpDown size={16} className="flex-shrink-0" />
          </button>
        </ComboboxTrigger>
        <ComboboxContent
          align="start"
          sideOffset={2}
          collisionPadding={8}
          className={MULTI_COMBOBOX_CONTENT_CLASSNAME}
          shouldFilter={false}
        >
          <ComboboxInput
            value={query}
            onValueChange={setQuery}
            placeholder={field.placeholder ?? `Search ${codeType} codes...`}
            className="placeholder:text-sm"
          />
          <ComboboxList className="max-h-72 p-1">
            {query.trim().length === 0 && options.length === 0 ? (
              <div className="px-2 py-3 text-sm text-primary-500 dark:text-primary-400">
                Start typing to search {codeType} codes.
              </div>
            ) : options.length === 0 && !canCreate ? (
              <div className="px-2 py-3 text-sm text-primary-500 dark:text-primary-400">
                {isFetching ? 'Searching...' : 'No matching codes.'}
              </div>
            ) : (
              <ComboboxGroup>
                {options.map((option) => (
                  <ComboboxSelectableItem
                    key={option.code}
                    value={codeSearchItemLabel(option)}
                    isSelected={selectedSet.has(option.code)}
                    onSelect={toggleCode(option.code)}
                  >
                    <div className="flex min-w-0 flex-col items-start gap-0.5">
                      <span className="font-mono text-[13px] font-semibold">
                        {option.code}
                      </span>
                      {option.description ? (
                        <span className="text-xs text-primary-500 dark:text-primary-400">
                          {option.description}
                        </span>
                      ) : null}
                    </div>
                  </ComboboxSelectableItem>
                ))}
                {canCreate ? (
                  <ComboboxSelectableItem
                    value={`__create__:${customValue}`}
                    isSelected={false}
                    onSelect={handleCreate}
                  >
                    <span>
                      Add custom {codeType} code &quot;{customValue}&quot;
                    </span>
                  </ComboboxSelectableItem>
                ) : null}
              </ComboboxGroup>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
      {values.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {values.map((code) => {
            const option = optionsByCode.get(code)
            return (
              <Badge
                key={code}
                variant="outline"
                title={option?.description}
                className="flex max-w-full items-center gap-1 pr-1"
              >
                <span className="max-w-48 truncate font-mono">{code}</span>
                <button
                  type="button"
                  aria-label={`Remove ${code}`}
                  onClick={toggleCode(code)}
                  className="rounded-sm p-0.5 text-primary-600 hover:bg-primary-300 hover:text-primary-900 dark:text-primary-300 dark:hover:bg-primary-700 dark:hover:text-primary-100"
                >
                  <HiMiniXMark size={12} />
                </button>
              </Badge>
            )
          })}
          <button
            type="button"
            onClick={handleClearAll}
            className="text-xs font-medium text-blue-600 hover:opacity-70 dark:text-blue-400"
          >
            Clear all
          </button>
        </div>
      ) : null}
    </div>
  )
}

const NaicsCodeMultiComboboxField = <U extends FieldValues, K extends Path<U>>(
  props: MultiComboboxFieldProps<U, K>,
) => {
  const { data, query, setQuery, isFetching } = useNAICSCodeSearch({
    initialCodes: props.value ?? [],
  })
  return (
    <CodeSearchMultiComboboxField
      {...props}
      codeType="NAICS"
      options={data ?? []}
      query={query}
      setQuery={setQuery}
      isFetching={isFetching}
    />
  )
}

const PscCodeMultiComboboxField = <U extends FieldValues, K extends Path<U>>(
  props: MultiComboboxFieldProps<U, K>,
) => {
  const { data, query, setQuery, isFetching } = usePSCCodeSearch({
    initialCodes: props.value ?? [],
  })
  return (
    <CodeSearchMultiComboboxField
      {...props}
      codeType="PSC"
      options={data ?? []}
      query={query}
      setQuery={setQuery}
      isFetching={isFetching}
    />
  )
}

const TextInputField: React.FC<{
  value: string
  onChange: (value: string) => void
  placeholder?: string
  type?: React.HTMLInputTypeAttribute
}> = ({ value, onChange, placeholder, type }) => {
  const handleValueChange = useCallback(
    (
      e:
        | React.ChangeEvent<HTMLInputElement>
        | React.FormEvent<HTMLInputElement>,
    ) => {
      onChange(e.currentTarget.value)
    },
    [onChange],
  )

  return (
    <Input
      value={value || ''}
      onChange={handleValueChange}
      onInput={handleValueChange}
      type={type}
      placeholder={placeholder}
      className="dark:bg-default-dark"
    />
  )
}

const DATE_FIELD_CALENDAR_CLASS_NAMES = {
  month: 'space-y-1',
  caption:
    'flex items-center justify-between pt-0 relative items-center text-primary-900 dark:text-primary-100',
  head_cell:
    'flex items-center justify-center text-primary-900 dark:text-primary-100 w-7 font-normal text-[0.8rem]',
  row: 'flex w-full mt-0.5',
  cell: 'flex items-center justify-center h-7 w-7 text-center text-primary-900 dark:text-primary-100 text-sm p-0 relative focus-within:relative focus-within:z-20',
  day: 'flex h-7 w-7 items-center justify-center rounded-md p-1 font-normal transition duration-100 hover:bg-primary-100 focus:outline-none focus:ring-0 disabled:hover:bg-white aria-selected:opacity-100 dark:hover:bg-primary-700 dark:disabled:hover:bg-primary-800',
}

const DateField: React.FC<{
  value: string | null
  onChange: (value: string | null) => void
  placeholder?: string
  mode?: EntityDateFieldMode
}> = ({ value, onChange, placeholder, mode = 'datetime' }) => {
  const [open, setOpen] = useState<boolean>(false)
  const [draftDate, setDraftDate] = useState<Date | undefined>(undefined)
  const selectedDate = useMemo(() => parseEntityDateFieldValue(value), [value])
  const displayValue = formatEntityDateFieldValue(value, mode)

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen)
      setDraftDate(nextOpen ? selectedDate : undefined)
    },
    [selectedDate],
  )

  const handleClear = useCallback(() => {
    onChange(null)
    setDraftDate(undefined)
    setOpen(false)
  }, [onChange])

  const handleConfirm = useCallback(() => {
    onChange(serializeEntityDateFieldValue(draftDate, mode))
    setOpen(false)
  }, [draftDate, mode, onChange])

  if (mode === 'datetime') {
    return (
      <DateTimePickerV2
        date={selectedDate}
        onSelect={(selected) =>
          onChange(serializeEntityDateFieldValue(selected, 'datetime'))
        }
        placeholder={placeholder ?? 'Select date and time...'}
      />
    )
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={twMerge(
            TEXT_INPUT_CLASSNAME,
            'flex items-center justify-between gap-2 text-left',
          )}
        >
          <span className="flex min-w-0 items-center gap-2">
            <LuCalendar
              size={16}
              className="flex-shrink-0 text-primary-500 dark:text-primary-400"
            />
            <span
              className={twMerge(
                'truncate',
                displayValue
                  ? 'text-primary-900 dark:text-primary-100'
                  : 'text-primary-500 dark:text-primary-400',
              )}
            >
              {displayValue ??
                placeholder ??
                (mode === 'date'
                  ? 'Select date...'
                  : 'Select date and time...')}
            </span>
          </span>
          <HiChevronUpDown size={16} className="flex-shrink-0" />
        </button>
      </Popover.Trigger>
      <Popover.Content
        align="start"
        collisionPadding={12}
        className="flex w-fit max-h-[var(--radix-popover-content-available-height)] flex-col overflow-hidden p-0"
        portal={true}
      >
        <div className="min-h-0 overflow-y-auto">
          <DaypickerCalendar
            date={draftDate}
            onSelect={setDraftDate}
            showTime={false}
            calendarClassName="p-2 pb-1"
            calendarClassNames={DATE_FIELD_CALENDAR_CLASS_NAMES}
            timeClassName="px-3 pb-2 pt-2"
          />
        </div>
        <div className="flex shrink-0 items-center justify-between gap-2 border-t border-primary-200 p-2 dark:border-primary-700">
          <Button
            type="button"
            variant="outline-compact"
            onClick={handleClear}
            disabled={!selectedDate && !draftDate}
            className="flex-1"
          >
            <HiMiniXMark className="h-4 w-4" />
            Clear
          </Button>
          <Button
            type="button"
            variant="primary-compact"
            onClick={handleConfirm}
            disabled={!draftDate}
            className="flex-1"
          >
            <HiMiniCheck className="h-4 w-4" />
            Apply
          </Button>
        </div>
      </Popover.Content>
    </Popover>
  )
}

const TagMultiField: React.FC<{
  value: UUID[] | null
  onChange: (value: UUID[]) => void
}> = ({ value, onChange }) => {
  const tagIds = value ?? []
  const handleChange = useCallback(
    (nextTagIds: string[]) => {
      onChange(nextTagIds as UUID[])
    },
    [onChange],
  )

  return (
    <div>
      <TagComboboxMulti
        value={tagIds}
        onChange={handleChange}
        emptyLabel="Select tags..."
        searchPlaceholder="Search tags..."
        selectedLabel={
          tagIds.length === 1
            ? '1 tag selected'
            : `${tagIds.length} tags selected`
        }
        triggerVariant="field"
        comboboxContentProps={{
          align: 'start',
          side: 'bottom',
          sideOffset: 2,
          collisionPadding: 8,
          className:
            'w-[var(--radix-popover-trigger-width)] min-w-[var(--radix-popover-trigger-width)] max-w-[var(--radix-popover-trigger-width)] sm:max-w-none',
        }}
      />
      {tagIds.length > 0 ? (
        <TagList tagIds={tagIds} className="mt-2 flex-wrap gap-1.5" />
      ) : null}
    </div>
  )
}

const TextareaField: React.FC<{
  value: string
  onChange: (value: string) => void
  placeholder?: string
}> = ({ value, onChange, placeholder }) => {
  return (
    <ActivityDetailsEditor
      value={value || ''}
      onChange={onChange}
      placeholder={placeholder}
      showToolbar={false}
      className="rounded-md bg-primary-50 dark:bg-primary-900"
      editorContentClassName="min-h-24 px-3 py-2"
    />
  )
}

export const FieldInput = <U extends FieldValues, K extends FieldPath<U>>(
  props: FieldInputProps<U, K>,
) => {
  const { field, control, companies, people, agencies, departments, files } =
    props
  return (
    <Controller<U, K>
      name={field.key as K}
      control={control}
      rules={{
        validate: field.required
          ? (value) => {
              if (
                value === null ||
                value === undefined ||
                value === '' ||
                (Array.isArray(value) && value.length === 0)
              ) {
                return `${field.label} is required`
              }
              return true
            }
          : undefined,
      }}
      render={({ field: { value, onChange } }) => {
        const type = field.type as FieldType
        switch (type) {
          case 'dropdown':
            return (
              <SelectOrCreate
                value={(value as string) || null}
                options={field.options ?? []}
                onChange={onChange}
                placeholder={field.placeholder ?? 'Select an option'}
                searchPlaceholder="Search options..."
                allowCreate={false}
                optionLabels={field.optionLabels}
                optionColors={field.optionColors}
              />
            )
          case 'combobox':
            return (
              <SelectOrCreate
                value={(value as string) || null}
                options={field.options ?? []}
                onChange={onChange}
                placeholder={field.placeholder ?? 'Select or type…'}
                optionColors={field.optionColors}
              />
            )
          case 'multi_combobox':
            if (String(field.key) === 'naics_codes') {
              return (
                <NaicsCodeMultiComboboxField
                  field={field}
                  control={control}
                  value={value as string[] | null}
                  onChange={onChange}
                />
              )
            }
            if (String(field.key) === 'psc_codes') {
              return (
                <PscCodeMultiComboboxField
                  field={field}
                  control={control}
                  value={value as string[] | null}
                  onChange={onChange}
                />
              )
            }
            return (
              <MultiComboboxField
                field={field}
                control={control}
                value={value as string[] | null}
                onChange={onChange}
              />
            )
          case 'company_reference':
            return (
              <CompanyReferenceField
                field={field}
                control={control}
                value={value as UUID | null}
                onChange={onChange}
                companies={companies}
              />
            )
          case 'person_reference':
            return (
              <PersonReferenceField
                field={field}
                control={control}
                value={value as UUID | null}
                onChange={onChange}
                people={people}
              />
            )
          case 'agency_reference':
            return (
              <AgencyReferenceField
                field={field}
                control={control}
                value={value as UUID | null}
                onChange={onChange}
                agencies={agencies}
              />
            )
          case 'department_reference':
            return (
              <DepartmentReferenceField
                field={field}
                control={control}
                value={value as UUID | null}
                onChange={onChange}
                departments={departments}
              />
            )
          case 'file_reference':
            return (
              <FileReferenceField
                field={field}
                control={control}
                value={value as UUID | null}
                onChange={onChange}
                files={files}
              />
            )
          case 'tag_multi':
            return (
              <TagMultiField
                value={value as UUID[] | null}
                onChange={onChange}
              />
            )
          case 'string_array':
            return (
              <StringArrayField
                field={field}
                control={control}
                value={value as string[] | null}
                onChange={onChange}
              />
            )
          case 'tag_input':
            return (
              <TagInputField
                field={field}
                control={control}
                value={value as string[] | null}
                onChange={onChange}
              />
            )
          case 'comma_separated_array':
            return (
              <CommaSeparatedArrayField
                field={field}
                control={control}
                value={value as string[] | null}
                onChange={onChange}
              />
            )
          case 'textarea':
            return (
              <TextareaField
                value={(value as string) || ''}
                onChange={onChange}
                placeholder={field.placeholder ?? 'Enter value'}
              />
            )
          case 'date':
            return (
              <DateField
                value={(value as string | null) || null}
                onChange={onChange}
                placeholder={field.placeholder}
                mode={field.dateMode ?? 'datetime'}
              />
            )
          case 'url':
            return (
              <TextInputField
                value={(value as string) || ''}
                onChange={onChange}
                type="url"
                placeholder={field.placeholder}
              />
            )
          case 'email':
            return (
              <TextInputField
                value={(value as string) || ''}
                onChange={onChange}
                type="email"
                placeholder={field.placeholder}
              />
            )
          case 'phone':
            return (
              <TextInputField
                value={(value as string) || ''}
                onChange={onChange}
                type="tel"
                placeholder={field.placeholder}
              />
            )
          default:
            return (
              <TextInputField
                value={(value as string) || ''}
                onChange={onChange}
                placeholder={field.placeholder ?? 'Enter value'}
              />
            )
        }
      }}
    />
  )
}

export { CommaSeparatedArrayField }

interface ResetButtonProps {
  onClick: () => void
}

export const ResetButton: React.FC<ResetButtonProps> = ({ onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-sm text-blue-600 dark:text-blue-400 hover:opacity-70"
    >
      Reset
    </button>
  )
}

interface FieldOption {
  entityId: UUID
  entityName: string
  entityCreatedAt: string
  value: string | string[]
  displayValue?: string
}

interface FieldSouceOptions {
  value: UUID | null
  fieldOptions: FieldOption[]
  onChange: (value: UUID | null) => void
}

const FieldItem: React.FC<FieldOption> = ({
  entityName,
  value,
  displayValue,
  entityCreatedAt,
}) => {
  return (
    <span className="flex items-center">
      <span className="text-primary-900 dark:text-primary-100 max-w-[200px] truncate">
        {displayValue ?? (Array.isArray(value) ? value.join(', ') : value)}
      </span>
      <span className="flex items-center text-primary-700 dark:text-primary-300 flex-shrink-0">
        <span className="max-w-[200px] truncate">&nbsp;({entityName}</span>,{' '}
        {formatDistanceToNow(new Date(entityCreatedAt), { addSuffix: true })})
      </span>
    </span>
  )
}

const getFieldOptionDisplayValue = (option: FieldOption) =>
  option.displayValue ??
  (Array.isArray(option.value) ? option.value.join(', ') : option.value)

export const FieldSourceOptions: React.FC<FieldSouceOptions> = ({
  value,
  fieldOptions,
  onChange,
}) => {
  const handleChange = useCallback(
    (value: string | undefined) => {
      onChange((value ?? null) as UUID | null)
    },
    [onChange],
  )

  const selectedOption = fieldOptions.find(
    (option) => option.entityId === value,
  )
  return (
    <Select value={value ?? undefined} onValueChange={handleChange}>
      <SelectTrigger className="text-[13px] h-7 w-auto px-2">
        {selectedOption ? (
          <FieldItem {...selectedOption} />
        ) : (
          <span className="text-primary-500 dark:text-primary-400">
            No option selected
          </span>
        )}
      </SelectTrigger>
      <SelectContent align="end">
        {fieldOptions
          .slice()
          .sort((a, b) =>
            getFieldOptionDisplayValue(a).localeCompare(
              getFieldOptionDisplayValue(b),
            ),
          )
          .map((option) => (
            <SelectItem
              key={option.entityId}
              value={option.entityId}
              className="text-sm"
            >
              <FieldItem {...option} />
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  )
}
